import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { useBackNavigation } from '@/hooks/use-back-navigation';
import { getBaseUrl } from '@/lib/api';

const formSchema = z.object({
  Title: z.string().min(1, 'Product name is required'),
  Barcode: z.string().min(1, 'Barcode is required'),
  Description: z.string().optional(),
  HSN: z.string().min(1, 'HSN code is required'),
  WarrantyPeriod: z.string().optional(),
  MRP: z.string().min(1, 'MRP is required'),
  CostPrice: z.string().min(1, 'Cost price is required'),
  CategoryID: z.string().min(1, 'Category is required'),
  UQCName: z.string().min(1, 'UOM is required'),
  DiscountPer: z.string().optional(),
  Discount: z.string().optional(),
  BrandName: z.string().min(1, 'Brand name is required'),
  Color: z.string().optional(),
  Style: z.string().optional(),
  Stylecode: z.string().optional(),
  Size: z.string().optional(),
  NetQty: z.string().optional(),
  MinStQty: z.string().optional(),
  SgstPer: z.string().min(1, 'SGST % is required'),
  CGstPer: z.string().min(1, 'CGST % is required'),
  IgstPer: z.string().min(1, 'IGST % is required'),
  isTraceable: z.boolean().default(false),
  isService: z.boolean().default(false),
});

type FormValues = z.infer<typeof formSchema>;

const ProductAdd = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Custom back navigation handler for ProductAdd
  const handleProductAddBack = () => {
    navigate('/products');
  };

  // Use back navigation hook with custom handler
  // Note: This page is accessed via direct route, so it handles its own back navigation
  // When accessed from Dashboard, it should show exit confirmation instead of navigating back
  useBackNavigation({
    onBack: handleProductAddBack,
    canGoBack: true,
    showExitConfirmation: true // Show exit confirmation when accessed from Dashboard
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      Title: '',
      Barcode: '',
      Description: '',
      HSN: '',
      WarrantyPeriod: '',
      MRP: '',
      CostPrice: '',
      CategoryID: '',
      UQCName: '',
      DiscountPer: '0',
      Discount: '0',
      BrandName: '',
      Color: '',
      Style: '',
      Stylecode: '',
      Size: '',
      NetQty: '1',
      MinStQty: '1',
      SgstPer: '3',
      CGstPer: '3',
      IgstPer: '6',
      isTraceable: false,
      isService: false,
    },
  });

  // Fetch categories
  const { data: categories, isLoading: isCategoriesLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await fetch(`${getBaseUrl()}/api/Categories`);
      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }
      return response.json();
    },
  });

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${getBaseUrl()}/api/Product`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer YOUR_TOKEN_HERE', // Replace with actual token
        },
        body: JSON.stringify([{
          ...data,
          Code: '3', // Generate or get from API
          CreatedOn: new Date().toISOString().split('T')[0],
          Addedby: 'admin', // Replace with actual user
          Articlecode: '13', // Generate or get from API
          Extra: '1',
          Mfd: '1',
        }]),
      });

      if (!response.ok) {
        throw new Error('Failed to add product');
      }

      toast({
        title: 'Success',
        description: 'Product added successfully',
      });
      navigate('/products');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to add product. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const generateBarcode = () => {
    const randomBarcode = Math.floor(10000 + Math.random() * 90000).toString();
    form.setValue('Barcode', randomBarcode);
  };

  return (
    <div className="container mx-auto py-8 px-4 max-w-5xl android-form-container keyboard-aware-container">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Add New Product</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">Fill in the details to add a new product to your inventory.</p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="Title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Product Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter product name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-4">
                <FormField
                  control={form.control}
                  name="Barcode"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>Barcode</FormLabel>
                      <div className="flex gap-2">
                        <FormControl>
                          <Input placeholder="Enter barcode" {...field} />
                        </FormControl>
                        <Button type="button" variant="outline" onClick={generateBarcode}>
                          NEW
                        </Button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="CategoryID"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {categories?.data?.map((category: any) => (
                          <SelectItem key={category.id} value={category.id.toString()}>
                            {category.Category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="Description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Enter product description" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Pricing Information */}
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="MRP"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>MRP (₹)</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="Enter MRP" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="CostPrice"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cost Price (₹)</FormLabel>
                    <FormControl>
                      <Input type="number" placeholder="Enter cost price" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="DiscountPer"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Discount %</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="0" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="Discount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Discount (₹)</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="0" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FormField
              control={form.control}
              name="HSN"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>HSN Code</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter HSN code" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="UQCName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>UOM</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select UOM" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="PIECES">PIECES</SelectItem>
                      <SelectItem value="LITERS">LITERS</SelectItem>
                      <SelectItem value="KGS">KGS</SelectItem>
                      <SelectItem value="METERS">METERS</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="BrandName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Brand Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter brand name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Product Details */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <FormField
              control={form.control}
              name="Color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter color" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="Style"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Style</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter style" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="Stylecode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Style Code</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter style code" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="Size"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Size</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter size" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Tax Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FormField
              control={form.control}
              name="SgstPer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>SGST %</FormLabel>
                  <FormControl>
                    <Input type="number" placeholder="Enter SGST %" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="CGstPer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CGST %</FormLabel>
                  <FormControl>
                    <Input type="number" placeholder="Enter CGST %" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="IgstPer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>IGST %</FormLabel>
                  <FormControl>
                    <Input type="number" placeholder="Enter IGST %" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Switches */}
          <div className="flex flex-col sm:flex-row gap-6">
            <FormField
              control={form.control}
              name="isTraceable"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Traceable</FormLabel>
                    <div className="text-sm text-gray-500">
                      Enable if this product needs to be traced
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isService"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Service</FormLabel>
                    <div className="text-sm text-gray-500">
                      Enable if this is a service instead of a product
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/products')}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Product
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default ProductAdd;
